import { useEffect, useRef, useState } from 'react';
import { useAuthHeader, useIsAuthenticated } from 'react-auth-kit';
import { useCartContext } from 'context/CartContext/CartContext';
import { useAuthCode } from 'context/AuthCodeContext/AuthCodeContext';
import { calculatePrice } from 'api/checkoutpage';
import { CalculatePriceResponse } from 'api/responseTypes';
import { captureException } from '@sentry/react';
import { mapCartTicketsToCalculatePriceTickets } from '../TicketSummary/PromoCode/utils/mapCartTicketsToCalculatePriceTickets';
import { PROMO_CODE_STORAGE_KEY } from '../TicketSummary/PromoCode/constants';

const DEBOUNCE_DELAY_MS = 100;

type PriceCalculationParams = {
  eventId: number;
  ticketsCount: number;
  promoCode: string | undefined;
  authCode: string | null;
  authToken: string | undefined;
};

type UsePriceCalculationOptions = {
  onAuthRequired?: () => void;
};

function is401Error(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 401;
}

export const usePriceCalculation = (options: UsePriceCalculationOptions = {}) => {
  const { onAuthRequired } = options;
  const getAuthHeader = useAuthHeader();
  const isAuthenticated = useIsAuthenticated();
  const authToken = getAuthHeader();
  const { tickets, eventId, promoCode, setPromoCode, clearPromoCode } = useCartContext();
  const { authCode } = useAuthCode();
  const [priceCalculation, setPriceCalculation] = useState<CalculatePriceResponse | null>(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const lastCalculationParams = useRef<PriceCalculationParams | null>(null);
  const isApplyingPromoCode = useRef(false);
  const isRemovingPromoCode = useRef(false);
  const previousEventId = useRef<number | undefined>(undefined);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (previousEventId.current !== undefined && previousEventId.current !== eventId) {
      setPriceCalculation(null);
      lastCalculationParams.current = null;
    }
    previousEventId.current = eventId;
  }, [eventId]);

  useEffect(() => {
    const fetchPriceCalculation = async () => {
      if (!eventId || !tickets.length) {
        return;
      }

      if (isApplyingPromoCode.current || isRemovingPromoCode.current) {
        return;
      }

      let currentPromoCode = promoCode;
      if (!currentPromoCode) {
        const storedPromoCode = localStorage.getItem(PROMO_CODE_STORAGE_KEY);
        if (storedPromoCode) {
          currentPromoCode = storedPromoCode;
        }
      }

      const paramsKey: PriceCalculationParams = {
        eventId,
        ticketsCount: tickets.length,
        promoCode: currentPromoCode,
        authCode,
        authToken,
      };

      if (
        lastCalculationParams.current &&
        lastCalculationParams.current.promoCode !== paramsKey.promoCode
      ) {
        lastCalculationParams.current = null;
      }

      if (
        lastCalculationParams.current &&
        lastCalculationParams.current.eventId === paramsKey.eventId &&
        lastCalculationParams.current.ticketsCount === paramsKey.ticketsCount &&
        lastCalculationParams.current.promoCode === paramsKey.promoCode &&
        lastCalculationParams.current.authCode === paramsKey.authCode &&
        lastCalculationParams.current.authToken === paramsKey.authToken
      ) {
        return;
      }

      lastCalculationParams.current = paramsKey;

      const paymentMethod = authCode ? 'MINI_PROGRAM' : 'QI_SERVICES_PAYMENT_GATEWAY';
      const requestTickets = mapCartTicketsToCalculatePriceTickets(tickets);

      setIsCalculatingPrice(true);
      try {
        const calculatedPrice = await calculatePrice({
          eventId,
          tickets: requestTickets,
          paymentMethod,
          ...(currentPromoCode && { promoCode: currentPromoCode }),
        });

        setPriceCalculation(calculatedPrice);

        if (currentPromoCode && !promoCode) {
          if (calculatedPrice.appliedPromoCodesAmount > 0) {
            setPromoCode(currentPromoCode, calculatedPrice.appliedPromoCodesAmount);
          } else {
            localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
          }
        } else if (promoCode) {
          if (calculatedPrice.appliedPromoCodesAmount > 0) {
            setPromoCode(promoCode, calculatedPrice.appliedPromoCodesAmount);
          } else {
            clearPromoCode();
            localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
          }
        }
      } catch (error: unknown) {
        if (is401Error(error) && onAuthRequired && !isAuthenticated()) {
          onAuthRequired();
        } else if (!is401Error(error)) {
          captureException(error);
        }
        lastCalculationParams.current = null;
      } finally {
        setIsCalculatingPrice(false);
      }
    };

    fetchPriceCalculation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, tickets, promoCode, authCode, authToken]);

  useEffect(() => {
    return () => {
      setPriceCalculation(null);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handlePriceCalculated = (response: CalculatePriceResponse) => {
    if (!eventId) {
      captureException(new Error('eventId is required for price calculation'));
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    isApplyingPromoCode.current = true;
    setPriceCalculation(response);
    const promoCodeToSet = localStorage.getItem(PROMO_CODE_STORAGE_KEY) || promoCode || undefined;
    lastCalculationParams.current = {
      eventId,
      ticketsCount: tickets.length,
      promoCode: promoCodeToSet,
      authCode,
      authToken,
    };
    if (response.appliedPromoCodesAmount > 0 && promoCodeToSet) {
      setPromoCode(promoCodeToSet, response.appliedPromoCodesAmount);
    } else if (response.appliedPromoCodesAmount === 0 && promoCode) {
      clearPromoCode();
      localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
    }
    timerRef.current = setTimeout(() => {
      isApplyingPromoCode.current = false;
      timerRef.current = null;
    }, DEBOUNCE_DELAY_MS);
  };

  const handlePromoCodeRemoved = async () => {
    if (!eventId) {
      captureException(new Error('eventId is required for price calculation'));
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    isRemovingPromoCode.current = true;
    lastCalculationParams.current = {
      eventId,
      ticketsCount: tickets.length,
      promoCode: undefined,
      authCode,
      authToken,
    };

    if (tickets.length > 0) {
      const paymentMethod = authCode ? 'MINI_PROGRAM' : 'QI_SERVICES_PAYMENT_GATEWAY';
      const requestTickets = mapCartTicketsToCalculatePriceTickets(tickets);

      setIsCalculatingPrice(true);
      try {
        const calculatedPrice = await calculatePrice({
          eventId,
          tickets: requestTickets,
          paymentMethod,
        });

        setPriceCalculation(calculatedPrice);
      } catch (error: unknown) {
        if (is401Error(error) && onAuthRequired && !isAuthenticated()) {
          onAuthRequired();
        } else if (!is401Error(error)) {
          captureException(error);
        }
        lastCalculationParams.current = null;
      } finally {
        setIsCalculatingPrice(false);
      }
    }

    timerRef.current = setTimeout(() => {
      isRemovingPromoCode.current = false;
      timerRef.current = null;
    }, DEBOUNCE_DELAY_MS);
  };

  return {
    priceCalculation,
    isCalculatingPrice,
    handlePriceCalculated,
    handlePromoCodeRemoved,
  };
};
